import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart, ChartsReferenceLine } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';

// 5 sensors to display on landing page
const DISPLAY_SENSORS = [
  { id: 'ph', label: 'pH', unit: '', min: 0, max: 14, optimalMin: 5.5, optimalMax: 6.5, color: '#8b5cf6', baseValue: 6.2 },
  { id: 'airTemp', label: 'Air Temp', unit: '°F', min: 60, max: 95, optimalMin: 70, optimalMax: 80, color: '#ef4444', baseValue: 75 },
  { id: 'humidity', label: 'Humidity', unit: '%', min: 20, max: 90, optimalMin: 55, optimalMax: 70, color: '#3b82f6', baseValue: 62 },
  { id: 'co2', label: 'CO₂', unit: 'ppm', min: 300, max: 2000, optimalMin: 800, optimalMax: 1200, color: '#22c55e', baseValue: 1050 },
  { id: 'vpd', label: 'VPD', unit: 'kPa', min: 0, max: 2.5, optimalMin: 0.8, optimalMax: 1.4, color: '#f97316', baseValue: 1.1 },
];

const HISTORY_LENGTH = 30;
const UPDATE_INTERVAL = 2000; // 2 seconds

export default function LandingSensorOverview() {
  const theme = useTheme();

  // Initialize sensor data with history
  const [sensorData, setSensorData] = React.useState(() => {
    const now = Date.now();
    const data = {};

    DISPLAY_SENSORS.forEach(sensor => {
      data[sensor.id] = {
        ...sensor,
        currentValue: sensor.baseValue,
        history: Array.from({ length: HISTORY_LENGTH }, (_, i) => {
          const variation = (Math.random() - 0.5) * (sensor.max - sensor.min) * 0.08;
          return {
            time: new Date(now - (HISTORY_LENGTH - 1 - i) * UPDATE_INTERVAL),
            value: Math.max(sensor.min, Math.min(sensor.max, sensor.baseValue + variation)),
          };
        }),
      };
    });

    return data;
  });

  // Update sensor data periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      setSensorData(prev => {
        const now = new Date();
        const newData = { ...prev };

        DISPLAY_SENSORS.forEach(sensor => {
          const prevSensor = prev[sensor.id];
          const range = sensor.max - sensor.min;

          // Random walk with mean reversion
          const meanReversionStrength = 0.08;
          const randomWalk = (Math.random() - 0.5) * range * 0.04;
          const meanReversion = (sensor.baseValue - prevSensor.currentValue) * meanReversionStrength;

          const newValue = Math.max(
            sensor.min,
            Math.min(sensor.max, prevSensor.currentValue + randomWalk + meanReversion)
          );

          // Update history
          const newHistory = [...prevSensor.history.slice(1), { time: now, value: newValue }];

          newData[sensor.id] = {
            ...prevSensor,
            currentValue: Number(newValue.toFixed(2)),
            history: newHistory,
          };
        });

        return newData;
      });
    }, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Prepare chart data
  const chartData = React.useMemo(() => {
    const data = [];
    const firstSensor = sensorData[DISPLAY_SENSORS[0].id];

    firstSensor.history.forEach((point, i) => {
      const dataPoint = { time: point.time };

      DISPLAY_SENSORS.forEach(sensor => {
        const sensorInfo = sensorData[sensor.id];
        const historyPoint = sensorInfo.history[i];
        const midpoint = (sensor.min + sensor.max) / 2;
        const halfRange = (sensor.max - sensor.min) / 2;
        const percentOff = ((historyPoint.value - midpoint) / halfRange) * 100;

        dataPoint[sensor.id] = historyPoint.value;
        dataPoint[`${sensor.id}_normalized`] = percentOff;
      });

      data.push(dataPoint);
    });

    return data;
  }, [sensorData]);

  const xAxisData = chartData.map(d => d.time);

  // Calculate fixed time window for smooth streaming (always show full width)
  const timeWindow = React.useMemo(() => {
    if (xAxisData.length === 0) return { min: new Date(), max: new Date() };
    // Always use a fixed 60 second window from the latest data point
    const maxTime = xAxisData[xAxisData.length - 1] || new Date();
    const minTime = new Date(maxTime.getTime() - 60000); // 60 seconds
    return { min: minTime, max: maxTime };
  }, [xAxisData]);

  // Create series for each sensor
  const series = DISPLAY_SENSORS.map(sensor => ({
    data: chartData.map(d => d[`${sensor.id}_normalized`]),
    label: sensor.label,
    color: sensor.color,
    showMark: false,
    curve: 'monotoneX',
    valueFormatter: (v, { dataIndex }) => {
      const actual = chartData[dataIndex];
      return actual ? `${actual[sensor.id].toFixed(2)} ${sensor.unit}` : '';
    },
  }));

  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.paper', height: '100%' }}>
      <CardContent sx={{ py: { xs: 0.5, md: 1 }, px: { xs: 1, md: 1.5 }, '&:last-child': { pb: { xs: 0.5, md: 1 } }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="column" spacing={{ xs: 0.5, md: 1 }} sx={{ flex: 1, minHeight: 0 }}>
          {/* Header - Compact */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
            <Typography
              variant="body2"
              component="h3"
              sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.8rem', md: '0.875rem' } }}
            >
              Live Sensor Overview
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: { xs: 0.75, md: 1 },
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'success.main',
              }}
            >
              <Box
                sx={{
                  width: { xs: 5, md: 6 },
                  height: { xs: 5, md: 6 },
                  borderRadius: '50%',
                  bgcolor: 'white',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'white', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.55rem', md: '0.65rem' } }}
              >
                LIVE
              </Typography>
            </Box>
          </Stack>

          {/* Chart area - takes most space, fills available height */}
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 120, md: 250 },
              minWidth: 0,
              '& > div': { height: '100% !important' },
              '& svg': { overflow: 'visible' },
            }}
          >
            <LineChart
              xAxis={[{
                data: xAxisData,
                scaleType: 'time',
                min: timeWindow.min,
                max: timeWindow.max,
                tickNumber: 4,
                tickLabelStyle: {
                  fontSize: 7,
                  fill: theme.palette.text.secondary,
                  fontFamily: 'Inter, sans-serif',
                },
              }]}
              yAxis={[{
                min: -50,
                max: 50,
                tickNumber: 5,
                tickLabelStyle: {
                  fontSize: 7,
                  fill: theme.palette.text.secondary,
                  fontFamily: 'Inter, sans-serif',
                },
                valueFormatter: (v) => `${v > 0 ? '+' : ''}${v}%`,
              }]}
              series={series}
              margin={{ top: 2, right: 2, bottom: 16, left: 28 }}
              skipAnimation
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                '& .MuiLineElement-root': {
                  strokeWidth: { xs: 2, md: 2.5 },
                  transition: 'none',
                },
                '& .MuiChartsAxis-root': {
                  transition: 'none',
                },
              }}
            >
              <ChartsReferenceLine
                y={0}
                lineStyle={{
                  stroke: theme.palette.text.secondary,
                  strokeWidth: 1.5,
                }}
              />
              <ChartsReferenceLine
                y={15}
                lineStyle={{
                  stroke: theme.palette.warning.main,
                  strokeWidth: 1,
                  strokeDasharray: '5 3',
                }}
              />
              <ChartsReferenceLine
                y={-15}
                lineStyle={{
                  stroke: theme.palette.warning.main,
                  strokeWidth: 1,
                  strokeDasharray: '5 3',
                }}
              />
            </LineChart>
          </Box>

          {/* Legend below chart - horizontal on mobile, more compact */}
          <Box
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              pt: { xs: 0.5, md: 1 },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                mb: 0.5,
                fontSize: { xs: '0.55rem', md: '0.6rem' },
              }}
            >
              Current Readings
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(5, 1fr)', sm: 'repeat(5, 1fr)' },
                gap: { xs: 0.5, md: 1 },
              }}
            >
              {DISPLAY_SENSORS.map(sensor => {
                const data = sensorData[sensor.id];
                const isOptimal = data.currentValue >= sensor.optimalMin && data.currentValue <= sensor.optimalMax;

                return (
                  <Box
                    key={sensor.id}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'center', md: 'center' },
                      gap: { xs: 0.25, md: 0.75 },
                      p: { xs: 0.5, md: 0.75 },
                      borderRadius: 1,
                      bgcolor: `${sensor.color}15`,
                      border: `1px solid ${sensor.color}40`,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 6, md: 8 },
                        height: { xs: 6, md: 8 },
                        borderRadius: '50%',
                        bgcolor: sensor.color,
                        flexShrink: 0,
                        boxShadow: `0 0 4px ${sensor.color}`,
                      }}
                    />
                    <Box sx={{ textAlign: { xs: 'center', md: 'left' }, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          display: 'block',
                          lineHeight: 1.1,
                          fontSize: { xs: '0.55rem', md: '0.65rem' },
                        }}
                      >
                        {sensor.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          color: isOptimal ? 'success.main' : 'warning.main',
                          fontSize: { xs: '0.5rem', md: '0.6rem' },
                          fontWeight: 600,
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {data.currentValue.toFixed(sensor.unit === '' ? 1 : sensor.unit === 'ppm' ? 0 : 1)} {sensor.unit}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
